import { ContentError } from '@ctw/shared/components/errors/content-error';
import { HorizontalDivider } from '@ctw/shared/components/horizontal-divider';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import {
  type TableOfContentsItem,
  TableOfContentsScrollSpy,
} from '@ctw/shared/components/navigation/table-of-contents-scroll-spy';
import type { CdaDocument } from '@ctw/shared/content/document-viewer/_internal/parsers/types';
import { SupportFormLink } from '@ctw/shared/content/support-form-link';
import { useContainerQuery } from '@ctw/shared/hooks/breakpoints';
import { useScrollSpy } from '@ctw/shared/hooks/use-scroll-spy';
import {
  DOCUMENT_RENDERER_CONTENT_TEST_ID,
  DOCUMENT_VIEWER_SECTION_CLASSNAME,
} from '@ctw/shared/test/ids';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { isNonEmptyArray } from '@ctw/shared/utils/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CdaParser } from '../../parsers/cda-parser';
import { ReactSectionRenderer } from './react-section-renderer';

interface ReactCdaRenderProps {
  cdaParser: CdaParser;
}

export const ReactCdaRenderer = ({ cdaParser }: ReactCdaRenderProps) => {
  const containerRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { onScroll, scrollSpy } = useScrollSpy();
  const [data, setData] = useState<CdaDocument | null>(null);
  const { breakpoints } = useContainerQuery({ containerRef });

  const tableOfContentsItems: Array<TableOfContentsItem> = useMemo(
    () =>
      data?.content?.sections.map((section) => ({
        title: section.title,
        urlParameterId: section.id,
        contentDomId: section.domId,
      })) ?? [],
    [data],
  );

  useEffect(() => {
    const buildAndSetData = async () => setData(cdaParser.toDocumentJson());
    void buildAndSetData();
  }, [cdaParser]);

  const sectionRenderers = useMemo(
    () =>
      (data?.content?.sections || []).map((section, index) => {
        return (
          <>
            {index !== 0 && <HorizontalDivider className={tw`my-0`} />}
            <ReactSectionRenderer key={section.id} section={section} />
          </>
        );
      }),
    [data?.content?.sections],
  );

  if (!data) {
    return <LoadingSpinner centered={true} />;
  }

  if (!isNonEmptyArray(tableOfContentsItems)) {
    return (
      <ContentError
        title="There is no content available to display for this document."
        className={tw`flex h-full max-h-full min-h-full w-full items-center justify-center`}
      >
        <span>If you believe this is an issue, please </span>
        <SupportFormLink buttonText="contact support" />.
      </ContentError>
    );
  }

  return (
    <article
      ref={containerRef}
      id={data?.metadata?.document?.id?.root}
      className={twx('flex w-full max-w-full flex-col-reverse gap-x-4 overflow-hidden', {
        'flex-row': !breakpoints.isAtMost.sm,
      })}
    >
      <div
        data-testid={DOCUMENT_RENDERER_CONTENT_TEST_ID}
        ref={contentRef}
        className={tw`cda-renderer-content no-scrollbar scroll-shadows flex flex-col gap-[24px] overflow-y-auto overflow-x-hidden p-5`}
        onScroll={onScroll}
      >
        {sectionRenderers}
      </div>
      <TableOfContentsScrollSpy
        title="Document Table of Contents"
        items={tableOfContentsItems}
        scrollSpy={scrollSpy}
        contentRef={contentRef}
        containerRef={containerRef}
        urlParameterName="documentSectionId"
        contentDomClass={DOCUMENT_VIEWER_SECTION_CLASSNAME}
      />
    </article>
  );
};
